import notesJson from "./generated/notes.json";
import melodiesJson from "./generated/melodies.json";

/**
 * 멜로디 음정/곡 카탈로그 — 메타는 `data/notes.tsv`(noteId→hz) + `data/melodies.tsv`(곡 정의)가 SSoT.
 * `npm run export:data` → `generated/{notes,melodies}.json`을 결합한다 (moduleDefinitions.ts 패턴).
 *
 * ⚠ 음역은 brickStrength.ts의 7음(NOTE_HZ, 미파괴 타격음 전용)과 **독립**이다 — 이쪽은 2옥타브+반음
 * 25음으로 유명 동요를 표현하기 위한 멜로디 챕터 전용 채널. 일반 챕터 강도→색→음은 무변경.
 */

/** noteId → 주파수(Hz). notes.tsv 2옥타브+반음(do4 261.63 … do6 1046.50). */
export const NOTE_HZ_BY_ID: Record<string, number> = Object.fromEntries(
  notesJson.map((n) => [n.noteId, n.hz]),
);

/** 곡 정의 — 음 id 시퀀스(공백 구분 notes를 파싱). */
export interface MelodyDef {
  readonly melodyId: string;
  readonly displayName: string;
  /** 곡의 음 순서 (notes.tsv noteId 어휘). */
  readonly noteIds: readonly string[];
}

/**
 * 곡 notes 문자열(공백 구분 noteId) → noteId 배열. 미존재 noteId면 throw —
 * 곡 데이터 오타를 export 직후 단위테스트/부팅에서 즉시 잡는다(데이터 무결성 게이트).
 */
export function parseMelodyNotes(melodyId: string, notes: string): string[] {
  const ids = notes.trim().split(/\s+/).filter(Boolean);
  for (const id of ids) {
    if (!(id in NOTE_HZ_BY_ID)) {
      throw new Error(`[noteCatalog] melody "${melodyId}"의 음 "${id}"가 notes.tsv에 없습니다`);
    }
  }
  return ids;
}

/** enable된 곡만 — notes 파싱 후 noteIds로 결합. 멜로디 챕터 곡 순환 풀(GameScene). */
export const ALL_MELODIES: readonly MelodyDef[] = melodiesJson
  .filter((m) => m.enable)
  .map((m) => ({
    melodyId: m.melodyId,
    displayName: m.displayName,
    noteIds: parseMelodyNotes(m.melodyId, m.notes),
  }));
